import React, { useState, useEffect, useContext, useMemo } from 'react';
import { courseDetailsList, downloadCourseHeaders, coursesOffered } from '../../ProData';
import { Table, Breadcrumb, Button, ButtonGroup, Row, Col } from 'react-bootstrap';
import Preloader from '../../components/Preloader/Preloader';
import { Link } from "react-router-dom";
import { CSVLink } from "react-csv";
import ReactToPrint from "react-to-print";
import { ProductContext } from '../../ProductProvider';
import { Helmet } from 'react-helmet-async';

export default function CourseView() {
  const { loggedUser, validateLogin } = useContext(ProductContext);
  const [loaded, setLoaded] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('All');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'table'
  const componentRef = React.useRef(null);
  const csvLinkEl = React.createRef();

  useEffect(() => {
    if (loggedUser.isAuthenticated) {
      setLoaded(true);
    } else {
      validateLogin();
    }
  }, [loggedUser]);

  const reactToPrintContent = React.useCallback(() => {
    return componentRef.current;
  }, []);

  const reactToPrintTrigger = React.useCallback(() => {
    return (
      <Button variant="outline-primary" size="sm" className="d-inline-flex align-items-center gap-1">
        <i className="fa fa-print"></i> Print Curriculum
      </Button>
    );
  }, []);

  const handleDownload = (event) => {
    event.preventDefault();
    if (csvLinkEl.current) {
      csvLinkEl.current.link.click();
    }
  };

  const filteredCourses = useMemo(() => {
    return courseDetailsList.filter(course => {
      const matchesSearch = 
        (course.courseName && course.courseName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (course.courseCode && course.courseCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (course.courseType && course.courseType.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesYear = selectedYear === 'All' || course.courseYear === selectedYear;

      return matchesSearch && matchesYear;
    });
  }, [searchTerm, selectedYear]);

  const totalCredits = useMemo(() => {
    return filteredCourses.reduce((sum, item) => sum + (parseInt(item.courseCredit) || 0), 0);
  }, [filteredCourses]);

  const uniqueYears = useMemo(() => {
    const years = new Set(courseDetailsList.map(c => c.courseYear).filter(Boolean));
    return ['All', ...Array.from(years)];
  }, []);

  const userNickname = (loggedUser?.nickname || '').toUpperCase();
  const matchedCourse = coursesOffered.find(
    (course) => course.courseCode && userNickname.includes(course.courseCode.toUpperCase())
  );
  const enrolledCourseName = matchedCourse ? matchedCourse.courseName : 'Bachelor of Theology';
  const enrolledCourseCode = matchedCourse ? matchedCourse.courseCode : 'BTH';

  return (
    <div className="course-view-page animate-fade-in-up">
      <Helmet>
        <title>{enrolledCourseName} ({enrolledCourseCode}) Curriculum - Student Portal - Vidyajyoti College of Theology</title>
        <meta name="description" content={`Course details and curriculum list for ${enrolledCourseName} (${enrolledCourseCode}) at Vidyajyoti College of Theology`} />
        <link rel="canonical" href="/course" />
      </Helmet>

      <Preloader show={!loaded} />

      {/* Breadcrumb */}
      <Breadcrumb>
        <Breadcrumb.Item>
          <Link to="/">
            <i className="fa fa-home" aria-hidden="true"></i> Dashboard
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Students</Breadcrumb.Item>
        <Breadcrumb.Item active>Courses</Breadcrumb.Item>
      </Breadcrumb>

      {/* Header & Action Toolbar */}
      <div className="page-title-section">
        <div className="page-title-wrapper">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <h4 className="m-0">
              <i className="fa fa-book text-primary"></i> {enrolledCourseName}
            </h4>
            <span className="badge primary" style={{ fontSize: '0.8rem' }}>
              {enrolledCourseCode}
            </span>
          </div>
          <div className="page-subtitle">
            Course Curriculum • Showing {filteredCourses.length} courses ({totalCredits} Total Credits)
          </div>
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          {/* View Toggle */}
          <ButtonGroup className="bg-light p-1 rounded-3" style={{ border: '1px solid var(--card-border)' }}>
            <Button
              variant={viewMode === 'grid' ? 'primary' : 'light'}
              size="sm"
              onClick={() => setViewMode('grid')}
              style={{ borderRadius: '8px' }}
              title="Card Grid View"
            >
              <i className="fa fa-th-large"></i> Grid
            </Button>
            <Button
              variant={viewMode === 'table' ? 'primary' : 'light'}
              size="sm"
              onClick={() => setViewMode('table')}
              style={{ borderRadius: '8px' }}
              title="Table View"
            >
              <i className="fa fa-table"></i> Table
            </Button>
          </ButtonGroup>

          <ReactToPrint
            content={reactToPrintContent}
            documentTitle={`Vidyajyoti_${enrolledCourseCode}_Curriculum`}
            trigger={reactToPrintTrigger}
          />

          <Button 
            variant="outline-primary" 
            size="sm" 
            onClick={handleDownload}
            className="d-inline-flex align-items-center gap-1"
          >
            <i className="fa fa-download"></i> CSV Export
          </Button>

          <CSVLink
            headers={downloadCourseHeaders}
            filename="Vidyajyoti_Courses.csv"
            data={filteredCourses}
            ref={csvLinkEl}
            style={{ display: 'none' }}
          />
        </div>
      </div>

      {/* Search & Filter Controls */}
      <div className="card p-3 mb-4 shadow-sm">
        <Row className="g-3 align-items-center">
          <Col xs={12} md={6} lg={5}>
            <div className="position-relative">
              <input
                type="text"
                className="form-control ps-5"
                placeholder="🔍 Search course name, code, or type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <i 
                className="fa fa-search position-absolute text-muted" 
                style={{ top: '50%', left: '16px', transform: 'translateY(-50%)' }}
              ></i>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="btn btn-sm position-absolute text-muted"
                  style={{ top: '50%', right: '10px', transform: 'translateY(-50%)', border: 'none', background: 'none' }}
                >
                  <i className="fa fa-times"></i>
                </button>
              )}
            </div>
          </Col>

          <Col xs={12} md={6} lg={7}>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="text-muted small fw-bold"><i className="fa fa-filter me-1"></i> Year Filter:</span>
              {uniqueYears.map((year, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedYear(year)}
                  className={`btn btn-sm ${selectedYear === year ? 'btn-primary' : 'btn-outline-secondary'}`}
                  style={{ borderRadius: '999px', fontSize: '0.8rem', padding: '4px 12px' }}
                >
                  {year === 'All' ? 'All Years' : year}
                </button>
              ))}
            </div>
          </Col>
        </Row>
      </div>

      {/* Course List Display (Print Ref wraps both) */}
      <div ref={componentRef}>
        {/* Program Enrolled Banner */}
        <div className="card p-3 mb-3 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.08) 0%, rgba(59, 130, 246, 0.03) 100%)', borderLeft: '4px solid #2563eb' }}>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <span className="text-muted small fw-bold text-uppercase">Program Curriculum</span>
              <h5 className="fw-bold text-primary mb-1 d-flex align-items-center gap-2">
                <span>{enrolledCourseName}</span>
                <span className="badge primary" style={{ fontSize: '0.75rem' }}>{enrolledCourseCode}</span>
              </h5>
              <div className="text-muted small">
                Registration ID: <strong className="text-dark">{loggedUser.nickname || '23BTH1001'}</strong> • Vidyajyoti College of Theology
              </div>
            </div>
            <div className="text-md-end">
              <span className="badge gold px-3 py-2 fw-bold" style={{ fontSize: '0.85rem' }}>
                <i className="fa fa-graduation-cap me-1"></i> {totalCredits} Total Credits
              </span>
            </div>
          </div>
        </div>

        {filteredCourses.length === 0 ? (
          <div className="card p-5 text-center">
            <i className="fa fa-search fa-3x text-muted mb-3"></i>
            <h5 className="fw-bold">No matching courses found</h5>
            <p className="text-muted small">Try adjusting your search keywords or year filter.</p>
            <div>
              <Button variant="outline-primary" size="sm" onClick={() => { setSearchTerm(''); setSelectedYear('All'); }}>
                Reset Filters
              </Button>
            </div>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="course-grid">
            {filteredCourses.map((item, index) => (
              <div key={index} className="course-card">
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="course-code-badge">{item.courseCode}</span>
                    <span className="badge primary" style={{ fontSize: '0.72rem' }}>
                      {item.courseCredit} Credits
                    </span>
                  </div>
                  <h5 className="course-title">{item.courseName}</h5>
                </div>

                <div className="course-footer">
                  <span className="d-inline-flex align-items-center gap-1">
                    <i className="fa fa-tag text-muted"></i> {item.courseType || 'Core'}
                  </span>
                  <span className="d-inline-flex align-items-center gap-1 fw-bold text-primary">
                    <i className="fa fa-calendar-o"></i> {item.courseYear || 'Theology'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="table-wrapper">
            <Table hover responsive className="m-0">
              <thead className="theadStyle">
                <tr>
                  <th style={{ width: '60px' }}>#</th>
                  <th>Course Code</th>
                  <th>Course Name</th>
                  <th>Credit</th>
                  <th>Category / Type</th>
                  <th>Academic Year</th>
                </tr>
              </thead>
              <tbody className="tbodyStyle">
                {filteredCourses.map((item, index) => (
                  <tr key={index} className="trStyle">
                    <th scope="row" className="text-muted">{index + 1}</th>
                    <td>
                      <span className="course-code-badge">{item.courseCode}</span>
                    </td>
                    <td className="fw-semibold" style={{ color: 'var(--txt-color)' }}>{item.courseName}</td>
                    <td>
                      <span className="badge primary">{item.courseCredit} Cr</span>
                    </td>
                    <td>{item.courseType}</td>
                    <td>
                      <span className="badge gold">{item.courseYear}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
